import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ChevronIcon } from '@src/Components/Icons';

import { getMonth } from '@src/Common/Constants/getFormatDate';
import BackgroundFixed from '../BackgroundFixed/BackgroundFixed';
import styles from './Calendar.module.scss';

interface CalendarProps {
  value: string;
  variant: string;
  onCancel: () => void;
  onOk: (dt: string) => void;
  customStyleGroupChoice?: React.CSSProperties;
  disableDate?: (date: Date) => boolean;
  disabledMonth?: boolean;
  disabledYear?: boolean;
}

const lastNumberDate = -62135683200000;
const thirtyOneDays = [1, 3, 5, 7, 8, 10, 12];
const listMonth = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const Calendar: React.FC<CalendarProps> = ({
  value,
  variant,
  onCancel,
  onOk,
  customStyleGroupChoice,
  disableDate,
  disabledMonth,
  disabledYear
}) => {
  const [dateNumber, setDateNumber] = useState(0);
  const [month, setMonth] = useState(3);
  const [year, setYear] = useState(2023);
  const [isShowListMonth, setIsShowListMonth] = useState(false);
  const [isShowListYear, setIsShowListYear] = useState(false);
  const [dtNumber, setDtNumber] = useState<{ date: number; time: number }[][]>(
    []
  );
  const [yearList, setYearList] = useState<number[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);

  const addZero = (number: number) => {
    if (number < 10) {
      return `0${number}`;
    } else {
      return `${number}`;
    }
  };

  const addYearZero = (number: number) => {
    const paddedNumber = `${number}`.padStart(4, '0');
    return `${paddedNumber}`;
  };

  const onListDate = useCallback(() => {
    const oneDay = 24 * 60 * 60 * 1000;
    const dt = new Date(`${addYearZero(year)}-${addZero(month)}-${addZero(1)}`);

    let lastDate = 30;
    if (thirtyOneDays.findIndex((item) => item === dt.getMonth() + 1) >= 0) {
      lastDate = 31;
    }

    let endTime = dt.getTime() + (lastDate - 1) * oneDay;
    const et = new Date(endTime);
    if (et.getDay() !== 6) {
      endTime += (6 - et.getDay()) * oneDay;
    }

    let startTime = dt.getTime();
    if (dt.getDay() !== 0) {
      startTime = dt.getTime() - dt.getDay() * oneDay;
    }
    let arr: { date: number; time: number }[][] = [];
    let arrSub: { date: number; time: number }[] = [];

    for (let i = startTime; i <= endTime; i += oneDay) {
      arrSub = [
        ...arrSub,
        {
          date: new Date(i).getDate(),
          time: i
        }
      ];
      if (arrSub.length === 7) {
        arr = [...arr, arrSub];
        arrSub = [];
      }
    }
    setDtNumber([...arr]);
    let arrYear: number[] = [];
    for (let j = 1; j <= new Date().getFullYear() + 20; j++) {
      arrYear = [...arrYear, j];
    }
    setYearList([...arrYear]);
  }, [month, year]);

  useEffect(() => {
    onListDate();
  }, [onListDate]);

  useEffect(() => {
    let newValue = null;
    const newDate = new Date();
    if (value === undefined || value === null || value === '') {
      const yyyy = newDate.getFullYear();
      const mm = `${newDate.getMonth() + 1}`.padStart(2, '0');
      const dd = `${newDate.getDate()}`.padStart(2, '0');

      newValue = `${yyyy}-${mm}-${dd}`;
    } else {
      newValue = value;
    }
    const valueSplit = newValue.split('-');
    const valueYear = valueSplit[0] ?? newDate.getFullYear();
    const valueMonth = valueSplit[1] ?? newDate.getMonth() + 1;
    const valueDate = valueSplit[2] ?? newDate.getDate();
    const dt = new Date(`${valueYear}-${valueMonth}-${valueDate}`);

    setMonth(dt.getMonth() + 1);
    setYear(dt.getFullYear());
    setDateNumber(dt.getTime());
  }, [value]);

  useEffect(() => {
    const scrollToSelectedYear = () => {
      if (
        (isShowListYear && containerRef.current) ||
        (isShowListMonth && containerRef.current)
      ) {
        const selectedYearElem = containerRef.current.querySelector(
          `.choice-item-${year}`
        );
        const selectedMontElem = containerRef.current.querySelector(
          `.choice-item-${listMonth[+month - 1]}`
        );

        if (selectedYearElem) {
          selectedYearElem.scrollIntoView({
            behavior: 'instant',
            block: 'center'
          });
        }

        if (selectedMontElem) {
          selectedMontElem.scrollIntoView({
            behavior: 'instant',
            block: 'center'
          });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    };

    scrollToSelectedYear();
  }, [isShowListYear, isShowListMonth, year, month]);

  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0;
  };

  const iconColorByBorderColor = ['general-line', 'black-border'];

  const handlePrevMonth = () => {
    if (!disabledMonth) {
      if (month - 1 <= 0 && year > 1) {
        setYear((prev) => prev - 1);
      }
      setMonth((prev) => (prev - 1 <= 0 ? 12 : prev - 1));
      setDateNumber(0);
    }
  };

  const handleNextMonth = () => {
    if (!disabledMonth) {
      if (month + 1 > 12) {
        setYear((prev) => prev + 1);
      }
      setMonth((prev) => (prev + 1 > 12 ? 1 : prev + 1));
      setDateNumber(0);
    }
  };

  const handlePrevYear = () => {
    if (year > 1 && !disabledYear) {
      setYear((prev) => prev - 1);
      setDateNumber(0);
    }
  };

  const handleNextYear = () => {
    if (!disabledYear) {
      setYear((prev) => prev + 1);
      setDateNumber(0);
    }
  };

  return (
    <div
      className={`${styles['container']} ${styles[variant]}`}
      data-testid="calendar"
    >
      <div className={styles['option-months-years']}>
        <div className={styles['option']}>
          <div
            className={`${styles['option-button']} ${styles['prev']} ${
              styles[disabledMonth ? 'disabled' : '']
            }`}
            onClick={handlePrevMonth}
          >
            <ChevronIcon
              color={
                !iconColorByBorderColor.includes(variant)
                  ? '#000000'
                  : '#F2F2F2'
              }
            />
          </div>
          <div className={styles['option-selection']}>
            <div
              className={`${styles['option-selection-label']} ${
                styles[disabledMonth ? 'disabled' : '']
              }`}
              onClick={() => setIsShowListMonth(true)}
            >
              {listMonth[month - 1]}{' '}
            </div>
            {isShowListMonth && !disabledMonth && (
              <>
                <BackgroundFixed onClick={() => setIsShowListMonth(false)} />
                <div ref={containerRef} className={styles['group-choice']}>
                  {listMonth.map((item, i: number) => (
                    <div
                      key={Number(i)}
                      className={styles['choice-item']}
                      onClick={() => {
                        setMonth(i + 1);
                        return setIsShowListMonth(false);
                      }}
                    >
                      {item === listMonth[+month - 1] ? (
                        <div
                          className={`choice-item-${listMonth[+month - 1]} ${
                            styles['choice-item-active']
                          }`}
                        >
                          {item}
                        </div>
                      ) : (
                        item
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div
            className={`${styles['option-button']} ${styles['next']} ${
              styles[disabledMonth ? 'disabled' : '']
            }`}
            onClick={handleNextMonth}
          >
            <ChevronIcon
              color={
                !iconColorByBorderColor.includes(variant)
                  ? '#000000'
                  : '#F2F2F2'
              }
            />
          </div>
        </div>
        <div className={styles['option']}>
          <div
            className={`${styles['option-button']} ${styles['prev']} ${
              styles[disabledYear ? 'disabled' : '']
            } ${year > 1 ? '' : styles['disabled']}`}
            onClick={handlePrevYear}
          >
            <ChevronIcon
              color={
                !iconColorByBorderColor.includes(variant)
                  ? '#000000'
                  : '#F2F2F2'
              }
            />
          </div>
          <div className={styles['option-selection']}>
            <div
              className={`${styles['option-selection-label']} ${
                styles[disabledYear ? 'disabled' : '']
              }`}
              onClick={() => setIsShowListYear(true)}
            >
              {year}{' '}
            </div>
            {isShowListYear && !disabledYear && (
              <>
                <BackgroundFixed onClick={() => setIsShowListYear(false)} />
                <div
                  ref={containerRef}
                  style={customStyleGroupChoice}
                  className={styles['group-choice']}
                >
                  {yearList.map((item, i: number) => (
                    <div
                      key={Number(i)}
                      className={styles['choice-item']}
                      onClick={() => {
                        setYear(item);
                        setDateNumber(0);
                        return setIsShowListYear(false);
                      }}
                    >
                      {item === year ? (
                        <div
                          className={`choice-item-${year} ${styles['choice-item-active']}`}
                        >
                          {item}
                        </div>
                      ) : (
                        item
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div
            className={`${styles['option-button']} ${styles['next']} ${
              styles[disabledYear ? 'disabled' : '']
            }`}
            onClick={handleNextYear}
          >
            <ChevronIcon
              color={
                !iconColorByBorderColor.includes(variant)
                  ? '#000000'
                  : '#F2F2F2'
              }
            />
          </div>
        </div>
      </div>
      <div className={styles['weeks']}>
        <div
          className={`${styles['week']} ${
            styles[disabledMonth ? 'disabled' : '']
          }`}
        >
          <div className={styles['day']}>Su</div>
          <div className={styles['day']}>Mo</div>
          <div className={styles['day']}>Tu</div>
          <div className={styles['day']}>We</div>
          <div className={styles['day']}>Th</div>
          <div className={styles['day']}>Fr</div>
          <div className={styles['day']}>Sa</div>
        </div>
        {dtNumber.map((days, key: number) => (
          <div className={styles['week']} key={Number(key)}>
            {days.map((item, subkey: number) => {
              const isDisabled =
                disableDate && disableDate(new Date(item.time));
              const isActive = dateNumber === item.time;
              const isWeekendDay = isWeekend(new Date(item.time));

              const monthDateEvent = listMonth[month - 1]?.substring(0, 3);

              const dateClassName = `${styles.date} ${
                isActive ? styles.active : ''
              } ${isDisabled ? styles.disabled : ''} ${
                isWeekendDay ? styles.weekend : ''
              } ${
                monthDateEvent !==
                getMonth(item.time)?.monthText?.substring(0, 3)
                  ? styles.disabled
                  : ''
              } `;

              return (
                <div
                  className={dateClassName}
                  key={Number(subkey)}
                  onClick={() => !isDisabled && setDateNumber(item.time)}
                >
                  {item.date}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className={styles['footer-action']}>
        <div
          className={`${styles['calendar-button']} ${styles['cancel-button']}`}
          onClick={onCancel}
        >
          Cancel
        </div>
        <div
          className={`${styles['calendar-button']} ${styles['apply-button']} ${
            dateNumber > lastNumberDate ? '' : styles['disabled']
          }`}
          onClick={() => {
            if (dateNumber > lastNumberDate) {
              const dt = new Date(dateNumber);
              const yyyy = `${dt.getFullYear()}`.padStart(4, '0');
              const mm = addZero(dt.getMonth() + 1);
              const dd = addZero(dt.getDate());
              onOk(`${yyyy}-${mm}-${dd}`);
            }
          }}
        >
          Apply
        </div>
      </div>
    </div>
  );
};

export default Calendar;
